import { mulberry32 } from '@/data/bookCatalog';
import {
  KRITIKERE,
  alleKritikere,
  sjangerVektFor,
  templateFor,
  type Kritiker,
  type KritikerId,
  type TerningTall,
} from '@/data/kritikere';
import type {
  Anmeldelse,
  Author,
  AuthorStats,
  ContractedBook,
  PlanlagtAnmeldelse,
} from '@/data/types';
import {
  ANMELDELSE_FORSINKELSE_MAX,
  ANMELDELSE_FORSINKELSE_MIN,
} from '@/config';
import type { Tier } from '@/state/GameState';

const TIER_MOD: Record<Tier, number> = {
  D: 0.9,
  C: 0.95,
  B: 1.0,
  A: 1.05,
  S: 1.1,
};

export function planleggAnmeldelser(
  isbn: string,
  utgivelsesDag: number,
  seed?: number,
): PlanlagtAnmeldelse[] {
  const rng = seed === undefined ? Math.random : mulberry32(seed);
  // Alle fire kritikerne lander på SAMME dag. Da blir anmeldelsene én
  // iscenesatt «anmelderdag» — ett moment of truth å grue/glede seg til —
  // i stedet for fire spredte toasts på tilfeldige dager. Forsinkelsen
  // ligger fortsatt i [MIN, MAX]-vinduet, så testene holder.
  const spenn = ANMELDELSE_FORSINKELSE_MAX - ANMELDELSE_FORSINKELSE_MIN + 1;
  const forsinkelse = ANMELDELSE_FORSINKELSE_MIN + Math.floor(rng() * spenn);
  const domDag = utgivelsesDag + forsinkelse;
  return alleKritikere().map((k) => ({
    isbn,
    kritikerId: k.id,
    dag: domDag,
  }));
}

export function beregnTerninger(
  forfatter: Author,
  bok: ContractedBook,
  kritiker: Kritiker,
  seed?: number,
  bias = 0,
): TerningTall {
  const statScore = vektetStatScore(forfatter.stats, kritiker.statVekt);
  const sjangerBonus = sjangerVektFor(kritiker, bok.genre);
  const tierMod = TIER_MOD[bok.tier];
  const raaScore = statScore * sjangerBonus * tierMod;

  const rng = seed === undefined ? Math.random : mulberry32(seed);
  const jitter = (rng() - 0.5) * 20;
  const justert = raaScore + jitter;

  const grunn = mapScoreTilTerninger(justert, rng);
  return anvendBias(grunn, bias);
}

export const SVEN_BIAS_SITATER: ReadonlyArray<string> = [
  '{forfatter}s debut løfter spørsmål om habilitet. Boken har sjarm, men bransje-tvilen henger som tåke.',
  'Det er noe modig over å gi seg selv en sjanse, men jurorerollen og forfatterrollen er ikke gode partnere.',
  'Når sjefredaktøren publiserer sin egen bok, blir vi i kritikerlauget tvunget til å lese den dobbelt så strengt. Det merkes.',
  'Vi har lest med velvilje, men interessekonflikten skjemmer ut det som kunne vært en helt grei roman.',
];

export function genererAnmeldelse(
  bok: ContractedBook,
  forfatter: Author,
  kritikerId: KritikerId,
  dag: number,
  seed?: number,
  bias = 0,
): Anmeldelse {
  const kritiker = KRITIKERE[kritikerId];
  const terninger = beregnTerninger(forfatter, bok, kritiker, seed, bias);
  let sitat: string;
  if (bias < 0) {
    const rng = seed === undefined ? Math.random : mulberry32(seed + 13);
    const idx = Math.floor(rng() * SVEN_BIAS_SITATER.length);
    const template = SVEN_BIAS_SITATER[Math.min(SVEN_BIAS_SITATER.length - 1, idx)];
    sitat = template.replace(/\{forfatter\}/g, forfatter.navn);
  } else {
    const template = templateFor(kritiker, bok.genre, terninger);
    sitat = template
      .replace(/\{forfatter\}/g, forfatter.navn)
      .replace(/\{tittel\}/g, bok.title);
  }
  return {
    kritikerId,
    terninger,
    sitat,
    dag,
  };
}

function anvendBias(grunn: TerningTall, bias: number): TerningTall {
  if (bias === 0) return grunn;
  const justert = grunn + bias;
  if (justert < 1) return 1;
  if (justert > 6) return 6;
  return justert as TerningTall;
}

export function terningerTilStjerner(terninger: TerningTall): number {
  return Math.max(1, Math.round((terninger * 5) / 6));
}

export function stjerneStreng(terninger: TerningTall): string {
  const stjerner = terningerTilStjerner(terninger);
  return '★'.repeat(stjerner) + '☆'.repeat(5 - stjerner);
}

function vektetStatScore(
  stats: AuthorStats,
  vekter: Partial<Record<keyof AuthorStats, number>>,
): number {
  let sum = 0;
  let vektSum = 0;
  for (const [key, vekt] of Object.entries(vekter) as Array<[keyof AuthorStats, number]>) {
    sum += stats[key] * vekt;
    vektSum += vekt;
  }
  if (vektSum === 0) return 0;
  return sum / vektSum;
}

function mapScoreTilTerninger(score: number, rng: () => number): TerningTall {
  const klampet = Math.max(0, Math.min(110, score));
  if (klampet < 30) return rng() < 0.5 ? 1 : 2;
  if (klampet < 50) return rng() < 0.5 ? 2 : 3;
  if (klampet < 70) return rng() < 0.5 ? 3 : 4;
  if (klampet < 85) return rng() < 0.5 ? 4 : 5;
  return rng() < 0.5 ? 5 : 6;
}
